import { Box, Button, Tooltip, Typography } from "@mui/material";
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import { DataGrid } from "@mui/x-data-grid";
import { formatCurrency } from "../../../helpers/financialHelpers";
import { useEffect, useState } from "react";
import NFSeModal from "./InvoiceShowBillingItems/NFSeModal";
import NFeModal from "./InvoiceShowBillingItems/NFeModal";
import NFSeShowModal from "./InvoiceShowBillingItems/NFSeShowModal";
import { getFiscalInvoiceService } from "../../../api/invoices";
import { getFiscalDetails } from "../../../api/fiscal";
import ErrorIcon from "@mui/icons-material/Error";

const InvoiceShowBillingItems = ({ invoice, onNFSeErrorChange }) => {
    const [openNFSeModal, setOpenNFSeModal] = useState(false);
    const [openNFeModal, setOpenNFeModal] = useState(false);
    const [openNFSeShowModal, setOpenNFSeShowModal] = useState(false);
    const [fiscalServiceData, setFiscalServiceData] = useState(null);
    const [fiscalData, setFiscalData] = useState(null);
    const [fiscalApiError, setfiscalApiError] = useState(null);

    const columns =
        [
            { field: 'itemName', headerName: 'Item', flex: 2 },
            { field: 'itemValue', headerName: 'Preço', flex: 1 },
            { field: 'itemQuantity', headerName: 'Quantidade', flex: 1 },
            { field: 'itemTotalValue', headerName: 'Total', flex: 1 },
        ];
    const rowsParts =
        [
            ...(invoice?.invoice_parts?.map(part => ({
                id: `part-${part.id}`,
                type: "part",
                itemId: part.part_id,
                itemName: part.name,
                itemValue: part.unit_value == null || part.unit_value === 0 ? '-' : `R$ ${formatCurrency(part.unit_value)}`,
                itemQuantity: part.quantity || '-',
                itemTotalValue: `R$ ${formatCurrency(part.subtotal_value)}` || '-',
            })) || []),
        ];
    const rowsServices = [
        ...(invoice?.invoice_services?.map(service => ({
            id: `service-${service.id}`,
            type: "service",
            itemId: service.service_id,
            itemName: service.name,
            itemValue: service.unit_value == null || service.unit_value === 0 ? '-' : `R$ ${formatCurrency(service.unit_value)}`,
            itemQuantity: service.quantity || '-',
            itemTotalValue: `R$ ${formatCurrency(service.subtotal_value)}` || '-',
        })) || []),
    ]
    const handleRowClick = (row) => {
        if (!row.itemId) return;
        const urlMap = {
            part: `/parts/${row.itemId}/edit`,
            service: `/services/${row.itemId}/edit`,
        };

        const url = urlMap[row.type];
        if (url) window.open(url, "_blank");
    };

    const handleOpenNFSeModal = () => setOpenNFSeModal(true);
    const handleCloseNFSeModal = () => setOpenNFSeModal(false);

    const handleOpenNFSeShowModal = () => setOpenNFSeShowModal(true);
    const handleCloseNFSeShowModal = () => setOpenNFSeShowModal(false);

    const handleOpenNFeModal = () => setOpenNFeModal(true);
    const handleCloseNFeModal = () => setOpenNFeModal(false);


    const fiscalStatus = fiscalServiceData?.results?.[0]?.status;
    const hasNFSeError = fiscalStatus && ["rejected", "interrupted", "denied"].includes(fiscalStatus);

    const invoiceCanceled = invoice?.status === "canceled";
    const nfseButtonDisabled = invoiceCanceled || !fiscalData;

    const tooltipMessage = invoiceCanceled ? "Não é possível emitir uma nota fiscal para uma fatura cancelada"
        : !fiscalData ? `Preencha as informações necessárias na tela de "Configurações fiscais" para a emissão da nota` : "";

    const fetchNFSeDetails = async () => {
        if (!invoice?.id) return;

        try {
            const result = await getFiscalInvoiceService(invoice.id);
            setFiscalServiceData(result);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (!invoice?.id) return;

        if (fiscalStatus !== "pending") return;

        const interval = setInterval(() => {
            fetchNFSeDetails();
        }, 10000);

        return () => clearInterval(interval);
    }, [fiscalStatus, invoice?.id]);

    useEffect(() => {
        if (!invoice?.id) return;

        fetchNFSeDetails();

        const fetchFiscalSettings = async () => {
            try {
                const result = await getFiscalDetails(invoice.id);
                const data = result?.results?.[0];
                setFiscalData(data);
            } catch (error) {
                setfiscalApiError({ errorMessage: error.message });
            }
        };

        fetchFiscalSettings();
    }, [invoice?.id]);

    useEffect(() => {
        onNFSeErrorChange?.(hasNFSeError);
    }, [hasNFSeError, onNFSeErrorChange]);


    return (
        <>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <ReceiptRoundedIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant='h5' sx={{ color: 'primary.main' }}>Itens na cobrança</Typography>
                </Box>
                {rowsParts.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <Box>
                            <Typography variant='h6'> Peças e Materiais</Typography>
                        </Box>
                        <Box>
                            <Tooltip title={tooltipMessage} arrow placement="right">
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        disabled={nfseButtonDisabled}
                                        onClick={handleOpenNFeModal}
                                    >
                                        Emitir nota (NF-e)
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', marginBottom: '2rem' }}>
                            <DataGrid
                                columns={columns}
                                rows={rowsParts}
                                hideFooter
                                disableColumnMenu
                                disableSelectionOnClick
                                loading={!invoice}
                                onRowClick={(params) => handleRowClick(params.row)}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeader': {
                                        px: '2rem',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderRight: 'none',
                                        px: '2rem',
                                    },
                                    '& .MuiDataGrid-columnSeparator': {
                                        display: 'none',
                                    },
                                    '& .MuiDataGrid-row': {
                                        borderBottom: '1px solid theme.palette.divider',
                                        cursor: 'pointer',
                                    },

                                    '.MuiDataGrid-columnHeaderTitle': {
                                        fontWeight: 'bold',
                                    },
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-cell:focus-within': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-columnHeader:focus': {
                                        outline: 'none',
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )}
                {rowsServices.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <Box>
                            <Typography variant='h6'> Serviços</Typography>
                        </Box>
                        {fiscalServiceData?.results?.length > 0 ? (
                            <Box>
                                <Button
                                    variant="outlined"
                                    onClick={handleOpenNFSeShowModal}
                                    color={openNFSeShowModal ? "success" : "primary"}
                                    size="large"
                                    sx={(theme) => ({
                                        ...(openNFSeShowModal && {
                                            backgroundColor: theme.palette.primary.selected,
                                            borderColor: theme.palette.primary.main,
                                            color: theme.palette.primary.main,
                                        }),
                                    })}
                                >
                                    Visualizar nota (NFS-e)
                                    {hasNFSeError && (
                                        <ErrorIcon
                                            sx={{
                                                position: "absolute",
                                                top: -13,
                                                right: 8,
                                                fontSize: "1.8rem",
                                                color: "error.main",
                                                backgroundColor: "white",
                                                borderRadius: "50%",
                                                border: `0px solid #fff`,
                                            }}
                                        />
                                    )}
                                </Button>
                            </Box>) : (
                            <Box>
                                <Tooltip title={tooltipMessage} arrow placement="right">
                                    <span>

                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleOpenNFSeModal}
                                            size="large"
                                            disabled={nfseButtonDisabled}
                                        >
                                            Emitir nota (NFS-e)
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                        <Box>
                            <DataGrid
                                columns={columns}
                                rows={rowsServices}
                                hideFooter
                                disableColumnMenu
                                disableSelectionOnClick
                                loading={!invoice}
                                onRowClick={(params) => handleRowClick(params.row)}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeader': {
                                        px: '2rem',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderRight: 'none',
                                        px: '2rem',
                                    },
                                    '& .MuiDataGrid-columnSeparator': {
                                        display: 'none',
                                    },
                                    '& .MuiDataGrid-row': {
                                        borderBottom: '1px solid theme.palette.divider',
                                        cursor: 'pointer',
                                    },

                                    '.MuiDataGrid-columnHeaderTitle': {
                                        fontWeight: 'bold',
                                    },
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-cell:focus-within': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-columnHeader:focus': {
                                        outline: 'none',
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
            <NFSeModal
                open={openNFSeModal}
                onClose={handleCloseNFSeModal}
                invoice={invoice}
                fiscalConfigData={fiscalData}
                fiscalApiError={fiscalApiError}
                setfiscalApiError={setfiscalApiError}
                onSuccess={fetchNFSeDetails}
            />
            <NFeModal
                open={openNFeModal}
                onClose={handleCloseNFeModal}
            />
            <NFSeShowModal
                open={openNFSeShowModal}
                onClose={handleCloseNFSeShowModal}
                invoice={invoice}
                fiscalService={fiscalServiceData?.results?.[0]}
                fiscalConfigData={fiscalData}
                onEmitAgain={() => {
                    handleCloseNFSeShowModal();
                    handleOpenNFSeModal();
                }}
            />
        </>
    )
}

export default InvoiceShowBillingItems;